import React from 'react';
import { Card, Descriptions, Tag, Button, Space, Popconfirm, message, Table, Divider } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, FileExcelOutlined, DownloadOutlined, ShareAltOutlined } from '@ant-design/icons';
import { deleteOrder } from '../store/ordersSlice';
import { fetchBDEs } from '../store/bdeSlice';
import { fetchShops } from '../store/shopsSlice';
import { formatCurrency } from '../utils/formatters';
import { API_URL } from '../utils/api';
import { generateInvoicePDF } from '../utils/invoiceGenerator';

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orders } = useSelector((state) => state.orders);
  const { users } = useSelector((state) => state.bde);
  const { shops } = useSelector((state) => state.shops);
  const [invoiceLoading, setInvoiceLoading] = React.useState(false);
  
  const order = orders.find(o => o.id === orderId);

  React.useEffect(() => {
    dispatch(fetchBDEs());
    dispatch(fetchShops());
  }, [dispatch]);

  const handleInvoice = async (action) => {
    try {
      setInvoiceLoading(true);
      await generateInvoicePDF(order, shop, bde, action);
      if (action === 'download') {
        message.success('Invoice downloaded successfully');
      }
    } catch (error) {
      message.error('Failed to generate invoice: ' + error.message);
    } finally {
      setInvoiceLoading(false);
    }
  };

  if (!order) {
    return <Card title="Order Details">Order not found</Card>;
  }

  const bde = users.find(u => u.id === order.bdeId);
  const shop = shops.find(s => s.id === order.shopId);

  const statusColor =
    order.status === 'Completed' ? 'green' :
    order.status === 'Pending' ? 'orange' :
    order.status === 'Processing' ? 'blue' :
    order.status === 'Cancelled' ? 'red' :
    order.status === 'Expected' ? 'gold' : 'default';

  const items = order.items && order.items.length > 0 ? order.items : [{
    productName: order.productName,
    quantity: order.quantity,
    mrp: order.mrp,
    amount: order.billingPrice || order.amount,
  }];

  const columns = [
    { title: 'Product', dataIndex: 'productName', key: 'productName' },
    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
    { title: 'MRP', dataIndex: 'mrp', key: 'mrp', render: (val) => formatCurrency(val) },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => formatCurrency(val) },
  ];

  const showInvoiceActions = ['Expected', 'Processing', 'Completed'].includes(order.status);

  return (
    <Card
      title={`Order Details: ${order.id}`}
      extra={
        <Space>
          {showInvoiceActions && (
            <>
              <Button 
                icon={<DownloadOutlined />} 
                loading={invoiceLoading}
                onClick={() => handleInvoice('download')}
              >
                Invoice
              </Button>
              <Button 
                icon={<ShareAltOutlined />} 
                loading={invoiceLoading}
                onClick={() => handleInvoice('share')}
              >
                Share
              </Button>
            </>
          )}
          <Tag color={statusColor}>{order.status}</Tag>
          <Button onClick={() => navigate(`/admin/orders/edit/${order.id}`)}>Edit</Button>
          <Button onClick={() => navigate('/admin/orders')}>Back</Button>
          <Popconfirm
            title="Delete the order"
            description="Are you sure to delete this order?"
            onConfirm={async () => {
              try {
                await dispatch(deleteOrder(order.id)).unwrap();
                message.success('Order deleted');
                navigate('/admin/orders');
              } catch (err) {
                message.error('Failed to delete order: ' + err);
              }
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        </Space>
      }
    >
      <Descriptions bordered column={1} labelStyle={{ width: '150px' }}>
        <Descriptions.Item label="Order ID">{order.id}</Descriptions.Item>
        <Descriptions.Item label="BDE Name">{bde ? bde.name : order.bdeId}</Descriptions.Item>
        <Descriptions.Item label="Customer Name">{order.customerName}</Descriptions.Item>
        <Descriptions.Item label="Shop Name">{shop ? `${shop.name} (${shop.pincode})` : order.shopId || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="GSTIN">{order.gstIn || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Payment Method">{order.paymentMethod || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Amount">{formatCurrency(order.amount)}</Descriptions.Item>
        <Descriptions.Item label="Status"><Tag color={statusColor}>{order.status}</Tag></Descriptions.Item>
        <Descriptions.Item label="Created At">{order.createdAt}</Descriptions.Item>
        <Descriptions.Item label="Payment Proof">
            {order.paymentProof ? (
                <a href={order.paymentProof.startsWith('http') ? order.paymentProof : `${API_URL}${order.paymentProof}`} target="_blank" rel="noopener noreferrer">
                    View Proof
                </a>
            ) : 'Not uploaded'}
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left">Order Items</Divider>
      <Table 
        dataSource={items} 
        columns={columns} 
        pagination={false} 
        rowKey={(record) => record.productName + record.quantity} // unique key fallback
      />
    </Card>
  );
};

export default OrderDetails;
